import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator
} from '#/components/ui/command';

import React from 'react';
import { SelectItemType } from 'types/common';
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '#/components/ui/popover';
import { Button } from '#/components/ui/button';
import { cn } from '#/lib/utils';
import { Icons } from '#/components/icons';
import { Checkbox } from './checkbox';

interface Props {
  label?: string;
  disabled?: boolean;
  required?: boolean;
  helperText?: string;
  items: SelectItemType[];
  defaultText?: string;
  className?: string;
  onAdd?: () => void;
  input?: string;
  setInput?: React.Dispatch<React.SetStateAction<string>>;
  handleInput?: any;
  value: SelectItemType[];
  onChange: (value: SelectItemType[]) => void;
}

export default function MultiSelect({
  label,
  disabled,
  items,
  helperText,
  onAdd,
  required = true,
  defaultText = '선택하세요.',
  input,
  setInput,
  handleInput,
  className,
  value,
  onChange
}: Props) {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [open, setOpen] = React.useState(false);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const input = inputRef.current;
    if (input) {
      if (e.key === 'Delete' || e.key === 'Backspace') {
        if (input.value === '') {
          const newSelected = [...value];
          newSelected.pop();
          onChange(newSelected);
        }
      } else if (e.key === 'Escape') {
        input.blur();
      }
    }
  };

  return (
    <div className={className}>
      {label && (
        <label className="block mb-1">
          {required && <span className="text-destructive">*</span>} {label}
        </label>
      )}

      <Popover modal open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className={cn(
              'w-full h-auto justify-start hover:bg-inherit',
              !value.length && 'text-muted-foreground'
            )}
            disabled={disabled}
          >
            <div className="flex gap-1 flex-wrap">{defaultText}</div>
          </Button>
        </PopoverTrigger>

        <PopoverContent className="p-0" align="start">
          <Command
            filter={(value, search) => {
              const [label] = value.split('|');
              return value === 'add' || label.includes(search) ? 1 : 0;
            }}
            onKeyDown={handleKeyDown}
          >
            <CommandInput
              placeholder="검색"
              onInput={handleInput}
              onValueChange={setInput}
            />
            <CommandList>
              <CommandEmpty>검색 결과가 없습니다.</CommandEmpty>
              <CommandGroup className="max-h-64 overflow-auto">
                {items.map((item) => {
                  const isSelected = value.some((v) => v.value === item.value);
                  return (
                    <CommandItem
                      key={item.value}
                      value={`${item.label}|${item.value}`}
                      onSelect={() => {
                        if (isSelected) {
                          onChange(value.filter((v) => v.value !== item.value));
                        } else {
                          onChange([...value, item]);
                        }
                      }}
                    >
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={() => {
                          if (isSelected) {
                            onChange(
                              value.filter((v) => v.value !== item.value)
                            );
                          } else {
                            onChange([...value, item]);
                          }
                        }}
                        className="mr-2"
                      />
                      {item.label}
                    </CommandItem>
                  );
                })}
              </CommandGroup>

              {onAdd && <CommandSeparator />}
              {onAdd && (
                <CommandGroup>
                  <CommandItem
                    value="add"
                    onSelect={() => {
                      setOpen(false);
                      onAdd();
                    }}
                  >
                    <Icons.plusCircle className="mr-2 h-5 w-5" />
                    추가
                  </CommandItem>
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {helperText && (
        <p className="text-sm text-muted-foreground mt-1">{helperText}</p>
      )}
    </div>
  );
}
